<template>
  <div class="transmission-popover-header" @click.stop="noop">
    <div class="cancel-btn" @click="emits('close')">{{ $t('owork_base.close') }}</div>
    <div v-if="isInIosAPP()" class="cancel-btn export-btn" @click="manageExport">export</div>
    <div class="transmission-popover-header-title">{{ $t('owork_base.transmission_list') }}</div>
    <div v-if="needShowClearBtn" class="clear-all-btn" @click="clear">{{ $t('owork_base.transmission.clear') }}</div>
  </div>
  <a-tabs
    v-model:activeKey="activeKey"
    :tab-bar-gutter="32"
    destroy-inactive-tab-pane
    class="transmission-tabs"
    size="small"
    :centered="!isStandardScreen"
    @click.stop="noop"
  >
    <a-tab-pane
      :key="TransmissionType.DOWNLOAD"
      :tab="
        $t('owork_base.transmission.download_task') +
        (downloadUncompletedTaskNum > 0 ? ` ${downloadUncompletedTaskNum}` : '')
      "
      ><transmission-component
        v-if="activeKey === TransmissionType.DOWNLOAD"
        ref="downloadTransmissionRef"
        :uncompleted-task-num="downloadUncompletedTaskNum"
        :tasks="downloadDataSource"
        :is-upload="false"
        :task-id="taskId"
    /></a-tab-pane>
    <a-tab-pane
      :key="TransmissionType.UPLOAD"
      :tab="
        $t('owork_base.transmission.upload_task') + (uploadUncompletedTaskNum > 0 ? ` ${uploadUncompletedTaskNum}` : '')
      "
      force-render
    >
      <transmission-component
        v-if="activeKey === TransmissionType.UPLOAD"
        ref="uploadTransmissionRef"
        is-upload
        :uncompleted-task-num="uploadUncompletedTaskNum"
        :tasks="uploadDataSource"
    /></a-tab-pane>
  </a-tabs>
</template>

<script lang="ts" setup>
  import type { Ref } from 'vue'
  import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
  import TransmissionComponent from '@components/transmission/TransmissionComponent.vue'
  import { INSTANCE as FileDownloadManager } from '@/core/transmission/FileDownloadManager'
  import { INSTANCE as FileUploadManager } from '@/core/transmission/FileUploadManager'
  import { TaskState, TaskError } from '@/core/transmission/ITask'
  import type { AbsTask } from '@/core/transmission/AbsTask'
  import { INSTANCE as transmissionNoticeManager, NoticeReadState } from '@/core/transmission/TransmissionNoticeManager'
  import type {
    TaskManagerChangeListener,
    AbsFileTransmissionManager,
  } from '@/core/transmission/AbsFileTransmissionManager'
  import type { TaskInfo } from '@/core/transmission/ITask'
  import { INSTANCE as ToastManager } from '@/core/ToastManager'
  import { noop } from 'lodash'
  import { useSharedScreen } from '@/core/compositions/Screen'
  import { IOSEventName, NativeBridge } from '@/core/nativeAPP/IOSUtils'
  import { isInIosAPP } from '@/core/Utils'

  defineProps<{ taskId?: string }>()

  const emits = defineEmits(['close'])

  const { isStandardScreen } = useSharedScreen()

  enum TransmissionType {
    UPLOAD,
    DOWNLOAD,
  }

  const activeKey = ref<TransmissionType>(TransmissionType.DOWNLOAD)

  const downloadTasks = FileDownloadManager.getAllTask()
  const uploadTasks = FileUploadManager.getAllTask()

  const haveAliveTask = (taskList: AbsTask[]): boolean =>
    taskList.length > 0 && taskList.some((task) => task.state !== TaskState.SUCCESS && task.state !== TaskState.FAILED)

  if (
    (haveAliveTask(uploadTasks) && !haveAliveTask(downloadTasks)) ||
    (uploadTasks.length > 0 && downloadTasks.length === 0)
  ) {
    activeKey.value = TransmissionType.UPLOAD
  }

  const downloadDataSource: Ref<TaskInfo[]> = ref(FileDownloadManager.getAllTask().map((item) => item.toJson()))
  const uploadDataSource: Ref<TaskInfo[]> = ref(FileUploadManager.getAllTask().map((item) => item.toJson()))
  const downloadUncompletedTaskNum = computed(() => calculateUncompletedTaskNum(downloadDataSource))
  const uploadUncompletedTaskNum = computed(() => calculateUncompletedTaskNum(uploadDataSource))
  const downloadTransmissionRef = ref<InstanceType<typeof TransmissionComponent>>()
  const uploadTransmissionRef = ref<InstanceType<typeof TransmissionComponent>>()
  const needShowClearBtn = computed(() => {
    switch (activeKey.value) {
      case TransmissionType.DOWNLOAD:
        return downloadDataSource.value.length > 0 ? downloadTransmissionRef.value : undefined

      default:
        return uploadDataSource.value.length > 0 ? uploadTransmissionRef.value : undefined
    }
  })

  const clear = () => {
    needShowClearBtn.value?.onClickClearAll()
  }

  const uncompletedTaskStates = [TaskState.GOING, TaskState.PAUSE, TaskState.WAITING]

  const calculateUncompletedTaskNum = (taskArray: Ref<TaskInfo[]>) => {
    let uncompletedTaskNum = 0

    for (const task of taskArray.value) {
      uncompletedTaskStates.includes(task.state) && uncompletedTaskNum++
    }

    return uncompletedTaskNum
  }

  const promptErrorToast = (taskInfoArray: TaskInfo[]) => {
    // 若当前页面不在传输列表，传输列表中存在文管卸载异常的任务，则每次打开传输列表的时候弹出toast一次
    if (taskInfoArray.some((task) => task.failedReason === TaskError.NATIVE_SERVICE_BIND_FAILED)) {
      // 目前只支持加载异常的toast
      ToastManager.show('owork_base.transmission.file_load_abnormal_prompt')
    }
  }

  const createTaskChangeListener = (
    dataSource: Ref<TaskInfo[]>,
    transmissionManager: AbsFileTransmissionManager<AbsTask>
  ) => ({
    onAdd: (task: AbsTask) => {
      // dataSource.value.push(task.toJson())
      dataSource.value = transmissionManager.getAllTask().map((item) => item.toJson())
    },
    onUpdated: (task: AbsTask, needShow?: boolean) => {
      if (needShow === false) return

      dataSource.value = transmissionManager.getAllTask().map((item) => item.toJson())
      promptErrorToast(dataSource.value)
    },
    onExceedMaximumFileSize() {
      // do nothing
    },
  })

  const manageExport = () => {
    NativeBridge.getInstance().callNativeMethod(IOSEventName.EXPORT_FILE_LIST)
  }

  const downloadTaskChangeListener: TaskManagerChangeListener = createTaskChangeListener(
    downloadDataSource,
    FileDownloadManager
  )
  const uploadTaskChangeListener: TaskManagerChangeListener = createTaskChangeListener(
    uploadDataSource,
    FileUploadManager
  )

  onBeforeUnmount(() => {
    transmissionNoticeManager.isRead = NoticeReadState.READ
    FileDownloadManager.removeTaskManagerChangeListener(downloadTaskChangeListener)
    FileUploadManager.removeTaskManagerChangeListener(uploadTaskChangeListener)
  })
  onMounted(() => {
    FileDownloadManager.addTaskManagerChangeListener(downloadTaskChangeListener)
    FileUploadManager.addTaskManagerChangeListener(uploadTaskChangeListener)
  })
</script>

<style lang="stylus" scoped>

  .transmission-tabs
    :deep(.ant-tabs-nav)
      margin 0
      padding-left 20px
    :deep(.ant-tabs-tab-btn)
      font-size 14px
      color default_color
      line-height 19.88px
      font-weight bolder_font_weight
    :deep(.ant-tabs-tab-active .ant-tabs-tab-btn)
      color #000000
    :deep(.ant-tabs-ink-bar)
      background #000000
  .export-btn
    position absolute
    left 60px
    top 15px

  @import '../styles/h5Adaptive/Transmission.styl'
</style>
